'use client';

import { CheckCircle, ArrowRight, Brain, Zap, Mail } from 'lucide-react';

export function SolutionSection() {
	return (
		<section className="py-20 bg-gradient-to-r from-emerald-600 to-green-700 text-white">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="text-center mb-16">
					<h2 className="text-3xl md:text-4xl font-bold mb-4">
						How NOS Email Solves This
					</h2>
					<p className="text-xl text-emerald-100 max-w-3xl mx-auto">
						Send from your favorite tools like normal. Our AI instantly routes each
						email through the best infrastructure for maximum deliverability at minimum
						cost.
					</p>
				</div>

				{/* Email Routing Flow Infographic */}
				<div className="max-w-6xl mx-auto mb-16">
					<div className="grid md:grid-cols-3 gap-8 items-center">
						{/* Step 1: Send from tools */}
						<div className="text-center">
							<h3 className="text-lg font-semibold mb-6">1. Send Like Normal</h3>
							<div className="space-y-4">
								{/* Instantly Logo Placeholder */}
								<div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
									<div className="flex items-center justify-center space-x-2">
										<div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
											<Mail className="h-5 w-5 text-white" />
										</div>
										<span className="font-medium">Instantly</span>
									</div>
									<div className="text-sm text-emerald-200 mt-2">
										1,000 emails ready
									</div>
								</div>

								{/* Smartlead Logo Placeholder */}
								<div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
									<div className="flex items-center justify-center space-x-2">
										<div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
											<Zap className="h-5 w-5 text-white" />
										</div>
										<span className="font-medium">Smartlead</span>
									</div>
									<div className="text-sm text-emerald-200 mt-2">
										Campaign active
									</div>
								</div>

								{/* Other tools */}
								<div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
									<div className="flex items-center justify-center space-x-2">
										<div className="w-8 h-8 bg-gray-500 rounded-lg flex items-center justify-center">
											<Mail className="h-5 w-5 text-white" />
										</div>
										<span className="font-medium">Any Tool</span>
									</div>
									<div className="text-sm text-emerald-200 mt-2">
										SMTP/API ready
									</div>
								</div>
							</div>
						</div>

						{/* Step 2: AI Routing */}
						<div className="text-center">
							<h3 className="text-lg font-semibold mb-6">2. AI Smart Routing</h3>
							<div className="relative">
								<div className="bg-emerald-500/20 backdrop-blur-sm rounded-2xl p-6 border-2 border-emerald-400">
									<div className="flex items-center justify-center mb-4">
										<div className="w-12 h-12 bg-emerald-400 rounded-full flex items-center justify-center animate-pulse">
											<Brain className="h-6 w-6 text-emerald-900" />
										</div>
									</div>
									<div className="text-lg font-bold mb-2">NOS AI Router</div>
									<div className="text-sm text-emerald-200 space-y-1">
										<div className="animate-fade-in-out">
											Analyzing recipient MX...
										</div>
										<div className="animate-fade-in-out delay-1000">
											Checking 100+ providers...
										</div>
										<div className="animate-fade-in-out delay-2000">
											Selecting optimal route...
										</div>
									</div>
								</div>

								{/* Animated arrows */}
								<div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-4 hidden md:block">
									<ArrowRight className="h-8 w-8 text-emerald-300 animate-pulse" />
								</div>
								<div className="absolute right-full top-1/2 transform -translate-y-1/2 mr-4 hidden md:block">
									<ArrowRight className="h-8 w-8 text-emerald-300 animate-pulse" />
								</div>
							</div>
						</div>

						{/* Step 3: Best Infrastructure */}
						<div className="text-center">
							<h3 className="text-lg font-semibold mb-6">3. Best Infrastructure</h3>
							<div className="space-y-3">
								{/* Gmail */}
								<div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20 transform hover:scale-105 transition-transform">
									<div className="flex items-center justify-center space-x-2">
										<div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
											<Mail className="h-4 w-4 text-white" />
										</div>
										<span className="text-sm font-medium">Gmail</span>
										<span className="text-xs text-emerald-300">95% rate</span>
									</div>
								</div>

								{/* Outlook */}
								<div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20 transform hover:scale-105 transition-transform">
									<div className="flex items-center justify-center space-x-2">
										<div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
											<Mail className="h-4 w-4 text-white" />
										</div>
										<span className="text-sm font-medium">Outlook</span>
										<span className="text-xs text-emerald-300">93% rate</span>
									</div>
								</div>

								{/* SMTP Providers */}
								<div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20 transform hover:scale-105 transition-transform">
									<div className="flex items-center justify-center space-x-2">
										<div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
											<Mail className="h-4 w-4 text-white" />
										</div>
										<span className="text-sm font-medium">SMTP Pro</span>
										<span className="text-xs text-emerald-300">97% rate</span>
									</div>
								</div>

								{/* Cost indicator */}
								<div className="mt-4 p-2 bg-green-500/20 rounded-lg">
									<div className="text-xs text-green-300 font-semibold">
										💰 Cheapest option selected
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Benefits list */}
				<div className="grid md:grid-cols-2 gap-12 items-center">
					<div>
						<h3 className="text-2xl font-bold mb-6">Why This Works Better</h3>
						<div className="space-y-4">
							<div className="flex items-start space-x-3">
								<CheckCircle className="h-6 w-6 text-green-400 mt-1 flex-shrink-0" />
								<div>
									<h4 className="font-semibold">100+ Infrastructure Partners</h4>
									<p className="text-emerald-100">
										Google Workspace, Outlook, SMTP providers, dedicated servers
										- we use them all to find the perfect match
									</p>
								</div>
							</div>
							<div className="flex items-start space-x-3">
								<CheckCircle className="h-6 w-6 text-green-400 mt-1 flex-shrink-0" />
								<div>
									<h4 className="font-semibold">Real-Time Optimization</h4>
									<p className="text-emerald-100">
										AI analyzes deliverability every hour and adapts routing
										instantly - not outdated monthly reports
									</p>
								</div>
							</div>
							<div className="flex items-start space-x-3">
								<CheckCircle className="h-6 w-6 text-green-400 mt-1 flex-shrink-0" />
								<div>
									<h4 className="font-semibold">Works With Any Tool</h4>
									<p className="text-emerald-100">
										Instantly, Smartlead, Apollo, or custom SMTP - just point to
										our endpoints and we handle the rest
									</p>
								</div>
							</div>
							<div className="flex items-start space-x-3">
								<CheckCircle className="h-6 w-6 text-green-400 mt-1 flex-shrink-0" />
								<div>
									<h4 className="font-semibold">Improved Deliverability</h4>
									<p className="text-emerald-100">
										Achieve 15-30% better deliverability that beats
										single-provider setups consistently
									</p>
								</div>
							</div>
						</div>
					</div>

					{/* Comparison Table */}
					<div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
						<div className="text-center mb-6">
							<h4 className="text-lg font-semibold text-white mb-2">
								NOS Email vs Google or Outlook
							</h4>
						</div>

						{/* Table Structure */}
						<div className="overflow-hidden rounded-xl border border-white/20">
							{/* Header Row */}
							<div className="grid grid-cols-3 bg-white/5">
								<div className="p-4 border-r border-white/20">
									<span className="text-white/70 font-medium text-sm">
										Metric
									</span>
								</div>
								<div className="p-4 border-r border-white/20 bg-emerald-500/20">
									<span className="text-emerald-200 font-semibold text-center block">
										NOS Email
									</span>
								</div>
								<div className="p-4 bg-red-500/20">
									<span className="text-red-200 font-semibold text-center block">
										Google or Outlook
									</span>
								</div>
							</div>

							{/* Data Rows */}
							<div className="grid grid-cols-3 border-t border-white/20">
								<div className="p-4 border-r border-white/20">
									<span className="text-white/80 font-medium">Setup Time</span>
								</div>
								<div className="p-4 border-r border-white/20 bg-emerald-500/10">
									<span className="text-white font-medium text-center block">
										5 minutes
									</span>
								</div>
								<div className="p-4 bg-red-500/10">
									<span className="text-white font-medium text-center block">
										10+ hours
									</span>
								</div>
							</div>

							<div className="grid grid-cols-3 border-t border-white/20">
								<div className="p-4 border-r border-white/20">
									<span className="text-white/80 font-medium">
										Made for Cold Email
									</span>
								</div>
								<div className="p-4 border-r border-white/20 bg-emerald-500/10">
									<span className="text-white font-medium text-center block">
										YES
									</span>
								</div>
								<div className="p-4 bg-red-500/10">
									<span className="text-white font-medium text-center block">
										NO
									</span>
								</div>
							</div>

							<div className="grid grid-cols-3 border-t border-white/20">
								<div className="p-4 border-r border-white/20">
									<span className="text-white/80 font-medium">Monthly Cost</span>
								</div>
								<div className="p-4 border-r border-white/20 bg-emerald-500/10">
									<span className="text-white font-medium text-center block">
										Starting at $10
									</span>
								</div>
								<div className="p-4 bg-red-500/10">
									<span className="text-white font-medium text-center block">
										$720+ per month
									</span>
								</div>
							</div>

							<div className="grid grid-cols-3 border-t border-white/20">
								<div className="p-4 border-r border-white/20">
									<span className="text-white/80 font-medium">Mailboxes</span>
								</div>
								<div className="p-4 border-r border-white/20 bg-emerald-500/10">
									<span className="text-white font-medium text-center block">
										Unlimited
									</span>
								</div>
								<div className="p-4 bg-red-500/10">
									<span className="text-white font-medium text-center block">
										Pay per mailbox
									</span>
								</div>
							</div>
						</div>

						{/* Bottom highlight */}
						<div className="mt-6 text-center">
							<div className="inline-flex items-center px-4 py-2 bg-green-500/20 rounded-lg">
								<span className="text-green-300 font-semibold">
									🎯 Save money with better results
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Add custom CSS for animations */}
			<style jsx>{`
				@keyframes fade-in-out {
					0%,
					100% {
						opacity: 0.5;
					}
					50% {
						opacity: 1;
					}
				}
				.animate-fade-in-out {
					animation: fade-in-out 3s infinite;
				}
				.delay-1000 {
					animation-delay: 1s;
				}
				.delay-2000 {
					animation-delay: 2s;
				}
			`}</style>
		</section>
	);
}
